/**
 * Platform and Organization Plans — Elvoaq
 *
 * All prices in EUR. Payment via Stripe.
 * FREE tier: 300 MB storage, 5 photos per item.
 */

// ==================== PLAN TYPES ====================

export type PlatformPlanType = 'FREE' | 'STARTER' | 'COLLECTOR' | 'PRO' | 'BUSINESS' | 'ULTIMATE' | 'CUSTOM';
export type OrganizationPlanType = 'TEAM' | 'BUSINESS' | 'ENTERPRISE' | 'CUSTOM';

// ==================== FEATURE FLAGS ====================

export interface PlanFeatures {
  // Core Features
  maxItems: number; // -1 for unlimited
  maxCategories: number; // -1 for unlimited
  maxWorkspaces: number;
  maxTeams: number;
  maxMembersPerWorkspace: number;

  // Storage
  storageGB: number;
  maxImagesPerItem: number;
  maxFileSizeGB: number;

  // AI Features
  aiCategorization: boolean;
  aiValuePrediction: boolean;
  aiDuplicateDetection: boolean;
  aiReportsPerMonth: number; // -1 for unlimited
  aiChatMessagesPerMonth: number; // -1 for unlimited

  // Analytics
  basicAnalytics: boolean;
  advancedAnalytics: boolean;
  customReports: boolean;
  exportData: boolean;

  // Security
  twoFactorAuth: boolean;
  auditLogs: boolean;
  auditLogRetentionDays: number;
  sso: boolean;

  // Support
  emailSupport: boolean;
  prioritySupport: boolean;
  dedicatedManager: boolean;
  slaGuarantee: boolean;

  // Collaboration
  comments: boolean;
  realTimeSync: boolean;
  activityFeed: boolean;
  webhooks: boolean;
  apiAccess: boolean;
  apiRateLimit: number; // requests per minute

  // Customization
  customBranding: boolean;
  whiteLabel: boolean;
  customDomain: boolean;
}

// ==================== PLATFORM PLANS ====================

export interface PlatformPlan {
  id: PlatformPlanType;
  name: string;
  description: string;
  tagline: string;
  monthlyPrice: number; // EUR - 0 for free, -1 for custom
  yearlyPrice: number; // EUR - annual (discounted ~20%)
  stripeLink?: string; // Stripe checkout link for payment
  features: PlanFeatures;
  highlights: string[]; // Key selling points
  popular?: boolean;
}

export const PLATFORM_PLANS: Record<PlatformPlanType, PlatformPlan> = {

  // FREE
  FREE: {
    id: 'FREE',
    name: 'Free',
    description: 'Get started \u2014 catalog up to 30 items with basic features.',
    tagline: 'Free forever',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: {
      maxItems: 30,
      maxCategories: 5,
      maxWorkspaces: 1,
      maxTeams: 0,
      maxMembersPerWorkspace: 1,
      storageGB: 0.3, // 300 MB
      maxImagesPerItem: 5,
      maxFileSizeGB: 0.01, // 10 MB per file
      aiCategorization: true,
      aiValuePrediction: false,
      aiDuplicateDetection: false,
      aiReportsPerMonth: 0,
      aiChatMessagesPerMonth: 10,
      basicAnalytics: true,
      advancedAnalytics: false,
      customReports: false,
      exportData: false,
      twoFactorAuth: true,
      auditLogs: false,
      auditLogRetentionDays: 0,
      sso: false,
      emailSupport: true,
      prioritySupport: false,
      dedicatedManager: false,
      slaGuarantee: false,
      comments: false,
      realTimeSync: false,
      activityFeed: false,
      webhooks: false,
      apiAccess: false,
      apiRateLimit: 0,
      customBranding: false,
      whiteLabel: false,
      customDomain: false,
    },
    highlights: [
      'Up to 30 items',
      '5 custom categories',
      '300 MB storage',
      '5 photos per item',
      '10 AI messages/mo',
      'AI categorization',
      'Basic analytics',
      'Two-factor auth',
    ],
  },

  // STARTER
  STARTER: {
    id: 'STARTER',
    name: 'Starter',
    description: 'For hobbyists and small collectors getting serious.',
    tagline: 'Start collecting smarter',
    monthlyPrice: 9,
    yearlyPrice: 86,
    stripeLink: 'https://buy.stripe.com/cNifZh042gpY4IReXebo401',
    features: {
      maxItems: 250,
      maxCategories: 15,
      maxWorkspaces: 2,
      maxTeams: 0,
      maxMembersPerWorkspace: 1,
      storageGB: 2,
      maxImagesPerItem: 10,
      maxFileSizeGB: 0.025,
      aiCategorization: true,
      aiValuePrediction: false,
      aiDuplicateDetection: false,
      aiReportsPerMonth: 5,
      aiChatMessagesPerMonth: 50,
      basicAnalytics: true,
      advancedAnalytics: false,
      customReports: false,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: false,
      auditLogRetentionDays: 0,
      sso: false,
      emailSupport: true,
      prioritySupport: false,
      dedicatedManager: false,
      slaGuarantee: false,
      comments: true,
      realTimeSync: false,
      activityFeed: false,
      webhooks: false,
      apiAccess: false,
      apiRateLimit: 0,
      customBranding: false,
      whiteLabel: false,
      customDomain: false,
    },
    highlights: [
      'Up to 250 items',
      '2 GB storage',
      '10 photos per item',
      'AI categorization',
      'Export your data',
      'Comments',
    ],
  },

  // COLLECTOR
  COLLECTOR: {
    id: 'COLLECTOR',
    name: 'Collector',
    description: 'For dedicated collectors who need more room and smarter tools.',
    tagline: 'Most popular for collectors',
    monthlyPrice: 19,
    yearlyPrice: 182,
    stripeLink: 'https://buy.stripe.com/6oUaEX9EC1v4dfndTabo402',
    popular: true,
    features: {
      maxItems: 1000,
      maxCategories: 50,
      maxWorkspaces: 5,
      maxTeams: 1,
      maxMembersPerWorkspace: 3,
      storageGB: 10,
      maxImagesPerItem: 20,
      maxFileSizeGB: 0.05,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: 25,
      aiChatMessagesPerMonth: 100,
      basicAnalytics: true,
      advancedAnalytics: false,
      customReports: false,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: 30,
      sso: false,
      emailSupport: true,
      prioritySupport: false,
      dedicatedManager: false,
      slaGuarantee: false,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: false,
      apiAccess: false,
      apiRateLimit: 0,
      customBranding: false,
      whiteLabel: false,
      customDomain: false,
    },
    highlights: [
      'Up to 1,000 items',
      '10 GB storage',
      '20 photos per item',
      'AI value prediction',
      'Duplicate detection',
      'Real-time sync',
      '30-day audit logs',
    ],
  },

  // PRO
  PRO: {
    id: 'PRO',
    name: 'Pro',
    description: 'For power users and professionals managing large inventories.',
    tagline: 'Full-featured for pros',
    monthlyPrice: 29,
    yearlyPrice: 278,
    stripeLink: 'https://buy.stripe.com/00w4gz3ge2z81wFcP6bo403',
    features: {
      maxItems: 10000,
      maxCategories: -1,
      maxWorkspaces: 15,
      maxTeams: 5,
      maxMembersPerWorkspace: 10,
      storageGB: 50,
      maxImagesPerItem: 40,
      maxFileSizeGB: 0.1,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: -1,
      aiChatMessagesPerMonth: 500,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: true,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: 90,
      sso: false,
      emailSupport: true,
      prioritySupport: true,
      dedicatedManager: false,
      slaGuarantee: false,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: true,
      apiAccess: true,
      apiRateLimit: 120,
      customBranding: false,
      whiteLabel: false,
      customDomain: false,
    },
    highlights: [
      'Up to 10,000 items',
      '50 GB storage',
      '40 photos per item',
      'Unlimited AI reports',
      'Advanced analytics & reports',
      'API access (120 req/min)',
      'Priority support',
      'Team collaboration (10)',
    ],
  },

  // BUSINESS
  BUSINESS: {
    id: 'BUSINESS',
    name: 'Business',
    description: 'For teams and organizations that need scale, security, and control.',
    tagline: 'Built for teams',
    monthlyPrice: 49,
    yearlyPrice: 470,
    stripeLink: 'https://buy.stripe.com/9B64gz6sqc9Ifnv02kbo404',
    features: {
      maxItems: -1,
      maxCategories: -1,
      maxWorkspaces: -1,
      maxTeams: -1,
      maxMembersPerWorkspace: 25,
      storageGB: 200,
      maxImagesPerItem: 60,
      maxFileSizeGB: 0.5,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: -1,
      aiChatMessagesPerMonth: -1,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: true,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: 365,
      sso: true,
      emailSupport: true,
      prioritySupport: true,
      dedicatedManager: false,
      slaGuarantee: false,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: true,
      apiAccess: true,
      apiRateLimit: 300,
      customBranding: true,
      whiteLabel: false,
      customDomain: false,
    },
    highlights: [
      'Unlimited items',
      '200 GB storage',
      '60 photos per item',
      'Up to 25 team members',
      'SSO / Single Sign-On',
      'Custom branding',
      '365-day audit logs',
      'Webhooks & API (300 req/min)',
      'Priority support',
    ],
  },

  // ULTIMATE
  ULTIMATE: {
    id: 'ULTIMATE',
    name: 'Ultimate',
    description: 'The pinnacle \u2014 unlimited everything with premium support and customization.',
    tagline: 'No limits, no compromises',
    monthlyPrice: 99,
    yearlyPrice: 950,
    stripeLink: 'https://buy.stripe.com/8x26oHaIG5Lk1wF7uMbo405',
    features: {
      maxItems: -1,
      maxCategories: -1,
      maxWorkspaces: -1,
      maxTeams: -1,
      maxMembersPerWorkspace: -1,
      storageGB: 1000,
      maxImagesPerItem: -1,
      maxFileSizeGB: 1,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: -1,
      aiChatMessagesPerMonth: -1,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: true,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: -1,
      sso: true,
      emailSupport: true,
      prioritySupport: true,
      dedicatedManager: true,
      slaGuarantee: true,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: true,
      apiAccess: true,
      apiRateLimit: -1,
      customBranding: true,
      whiteLabel: true,
      customDomain: true,
    },
    highlights: [
      'Unlimited everything',
      '1 TB storage',
      'Unlimited photos per item',
      'Unlimited team members',
      'Dedicated account manager',
      'SLA guarantee',
      'White-label option',
      'Custom domain',
      'Unlimited API access',
    ],
  },

  // CUSTOM
  CUSTOM: {
    id: 'CUSTOM',
    name: 'Custom',
    description: 'Tailored plan for large organizations with specific needs.',
    tagline: "Let's talk",
    monthlyPrice: -1,
    yearlyPrice: -1,
    features: {
      maxItems: -1,
      maxCategories: -1,
      maxWorkspaces: -1,
      maxTeams: -1,
      maxMembersPerWorkspace: -1,
      storageGB: -1,
      maxImagesPerItem: -1,
      maxFileSizeGB: -1,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: -1,
      aiChatMessagesPerMonth: -1,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: true,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: -1,
      sso: true,
      emailSupport: true,
      prioritySupport: true,
      dedicatedManager: true,
      slaGuarantee: true,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: true,
      apiAccess: true,
      apiRateLimit: -1,
      customBranding: true,
      whiteLabel: true,
      customDomain: true,
    },
    highlights: [
      'Unlimited everything',
      'Custom storage',
      'Dedicated account manager',
      'SLA guarantee',
      'White-label option',
      'Custom domain',
      'On-premise deployment',
      'Custom integrations',
      'Tailored onboarding',
    ],
  },
};

// ==================== ORGANIZATION PLANS ====================

export interface OrganizationPlan {
  id: OrganizationPlanType;
  name: string;
  description: string;
  tagline: string;
  monthlyPricePerUser: number;
  yearlyPricePerUser: number;
  minUsers: number;
  features: PlanFeatures;
  highlights: string[];
  popular?: boolean;
}

export const ORGANIZATION_PLANS: Record<OrganizationPlanType, OrganizationPlan> = {
  TEAM: {
    id: 'TEAM',
    name: 'Team',
    description: 'Small teams collaborating on inventories.',
    tagline: 'Perfect for small teams',
    monthlyPricePerUser: 12,
    yearlyPricePerUser: 115,
    minUsers: 3,
    popular: true,
    features: {
      maxItems: 5000,
      maxCategories: -1,
      maxWorkspaces: 5,
      maxTeams: 5,
      maxMembersPerWorkspace: 10,
      storageGB: 25,
      maxImagesPerItem: 30,
      maxFileSizeGB: 0.1,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: 100,
      aiChatMessagesPerMonth: -1,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: false,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: 60,
      sso: false,
      emailSupport: true,
      prioritySupport: false,
      dedicatedManager: false,
      slaGuarantee: false,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: false,
      apiAccess: true,
      apiRateLimit: 60,
      customBranding: false,
      whiteLabel: false,
      customDomain: false,
    },
    highlights: [
      'Up to 10 team members',
      '5,000 items per workspace',
      '25 GB storage',
      'Role-based access',
      '60-day audit logs',
      'API access',
    ],
  },
  BUSINESS: {
    id: 'BUSINESS',
    name: 'Business',
    description: 'Growing organizations with advanced needs.',
    tagline: 'Scale your operations',
    monthlyPricePerUser: 24,
    yearlyPricePerUser: 230,
    minUsers: 5,
    features: {
      maxItems: 25000,
      maxCategories: -1,
      maxWorkspaces: 20,
      maxTeams: 20,
      maxMembersPerWorkspace: 50,
      storageGB: 100,
      maxImagesPerItem: 50,
      maxFileSizeGB: 0.5,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: -1,
      aiChatMessagesPerMonth: -1,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: true,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: 180,
      sso: true,
      emailSupport: true,
      prioritySupport: true,
      dedicatedManager: false,
      slaGuarantee: false,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: true,
      apiAccess: true,
      apiRateLimit: 200,
      customBranding: true,
      whiteLabel: false,
      customDomain: false,
    },
    highlights: [
      'Up to 50 team members',
      '25,000 items',
      '100 GB storage',
      'SSO',
      'Custom branding',
      'Priority support',
      '180-day audit logs',
    ],
  },
  ENTERPRISE: {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    description: 'Large organizations with complex requirements.',
    tagline: 'Custom enterprise solution',
    monthlyPricePerUser: -1,
    yearlyPricePerUser: -1,
    minUsers: 25,
    features: {
      maxItems: -1,
      maxCategories: -1,
      maxWorkspaces: -1,
      maxTeams: -1,
      maxMembersPerWorkspace: -1,
      storageGB: -1,
      maxImagesPerItem: -1,
      maxFileSizeGB: -1,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: -1,
      aiChatMessagesPerMonth: -1,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: true,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: 730,
      sso: true,
      emailSupport: true,
      prioritySupport: true,
      dedicatedManager: true,
      slaGuarantee: true,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: true,
      apiAccess: true,
      apiRateLimit: -1,
      customBranding: true,
      whiteLabel: true,
      customDomain: true,
    },
    highlights: [
      'Unlimited everything',
      'White-label',
      'Custom domain',
      'Dedicated manager',
      'SLA guarantee',
      'On-premise option',
    ],
  },
  CUSTOM: {
    id: 'CUSTOM',
    name: 'Custom',
    description: 'Tailored to your unique needs.',
    tagline: "Let's talk about your needs",
    monthlyPricePerUser: -1,
    yearlyPricePerUser: -1,
    minUsers: 1,
    features: {
      maxItems: -1,
      maxCategories: -1,
      maxWorkspaces: -1,
      maxTeams: -1,
      maxMembersPerWorkspace: -1,
      storageGB: -1,
      maxImagesPerItem: -1,
      maxFileSizeGB: -1,
      aiCategorization: true,
      aiValuePrediction: true,
      aiDuplicateDetection: true,
      aiReportsPerMonth: -1,
      aiChatMessagesPerMonth: -1,
      basicAnalytics: true,
      advancedAnalytics: true,
      customReports: true,
      exportData: true,
      twoFactorAuth: true,
      auditLogs: true,
      auditLogRetentionDays: -1,
      sso: true,
      emailSupport: true,
      prioritySupport: true,
      dedicatedManager: true,
      slaGuarantee: true,
      comments: true,
      realTimeSync: true,
      activityFeed: true,
      webhooks: true,
      apiAccess: true,
      apiRateLimit: -1,
      customBranding: true,
      whiteLabel: true,
      customDomain: true,
    },
    highlights: [
      'Fully customizable',
      'Custom development',
      'Dedicated infrastructure',
      'Premium SLA',
      'White-glove onboarding',
    ],
  },
};

// ==================== STORAGE ADD-ONS ====================

export interface StorageAddon {
  id: string;
  name: string;
  storageGB: number;
  monthlyPrice: number; // EUR
  yearlyPrice: number; // EUR
}

export const STORAGE_ADDONS: StorageAddon[] = [
  { id: 'storage_5gb', name: '5 GB Extra', storageGB: 5, monthlyPrice: 3, yearlyPrice: 29 },
  { id: 'storage_25gb', name: '25 GB Extra', storageGB: 25, monthlyPrice: 8, yearlyPrice: 77 },
  { id: 'storage_100gb', name: '100 GB Extra', storageGB: 100, monthlyPrice: 19, yearlyPrice: 182 },
  { id: 'storage_500gb', name: '500 GB Extra', storageGB: 500, monthlyPrice: 39, yearlyPrice: 374 },
];

// ==================== LIMITS ====================

export const LIMITS = {
  MAX_IMAGES_FREE: 5,
  MAX_IMAGES_STARTER: 10,
  MAX_IMAGES_COLLECTOR: 20,
  MAX_IMAGES_PRO: 40,
  MAX_IMAGES_BUSINESS: 60,
  MAX_IMAGES_ULTIMATE: -1,
  MAX_FILE_SIZE_FREE_MB: 10,
  MAX_FILE_SIZE_PAID_MB: 500,
  DEFAULT_RATE_LIMIT: 60,
};

// ==================== HELPERS ====================

export function getPlanByType(type: PlatformPlanType): PlatformPlan {
  return PLATFORM_PLANS[type];
}

export function getOrgPlanByType(type: OrganizationPlanType): OrganizationPlan {
  return ORGANIZATION_PLANS[type];
}

export function formatPrice(price: number, period: 'monthly' | 'yearly' = 'monthly'): string {
  if (price === -1) return 'Custom';
  if (price === 0) return 'Free';
  return `\u20ac${price}${period === 'monthly' ? '/mo' : '/yr'}`;
}

export function formatLimit(limit: number): string {
  if (limit === -1) return 'Unlimited';
  return limit.toLocaleString();
}

export function formatStorage(gb: number): string {
  if (gb === -1) return 'Custom';
  if (gb < 1) return `${Math.round(gb * 1024)} MB`;
  if (gb >= 1000) return `${(gb / 1000).toFixed(0)} TB`;
  return `${gb} GB`;
}
